
import {
    CheckCircleRounded,
    CloseOutlined,
    ColorLensOutlined,
    DeleteForeverOutlined,
    DoNotDisturbOnRounded,
    DriveFileRenameOutlineOutlined,
    GroupOutlined,
    HelpOutlineOutlined,
    KeyOutlined,
    RemoveCircleOutlineOutlined,
    SaveOutlined
} from "@mui/icons-material";

import {
    Autocomplete,
    Box,
    Button,
    Chip,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    Paper,
    Select,
    Skeleton,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";

import { GridColDef } from "@mui/x-data-grid";

import {
    SyntheticEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    DepartmentSimpleType,
    GroupRequestType,
    SecretarySimpleType,
    SelectedParametersType,
    UserType,
    VehicleGroupType,
    VehicleListType,
} from "../../../types/types";

import { ContentProps, Toast } from "primereact/toast";
import { SetterOrUpdater, useRecoilValue } from "recoil";
import { useCookies } from "react-cookie";

import {
    getFilteredList,
    setToastRefToOpen,
} from "../../../functions/functions";

import "./styles.css";
import GridTemplate from "../../../components/GridTemplate";
import Rmodal from "../../../components/Rmodal";
import { useLocation } from "react-router-dom";

import { SketchPicker } from "react-color";

import findAllVehiclesByCustomerIdAndSecretaryIdAndDepartmentIdListResponse from "../../../resources/Api/vehicle/findAllVehiclesByCustomerIdAndSecretaryIdAndDepartmentIdListResponse";
import findAllGroupsByCustomerIdAndSecretaryIdAndDepartmentIdCrudResponse from "../../../resources/Api/group/findAllGroupsByCustomerIdAndSecretaryIdAndDepartmentIdCrudResponse";
import updateGroupByCustomerAndDepartment from "../../../resources/Api/group/updateGroupByCustomerAndDepartment";
import deleteGroupByIdAndCustomerAndDepartment from "../../../resources/Api/group/deleteGroupByIdAndCustomerAndDepartment";
import saveGroupByCustomerAndDepartment from "../../../resources/Api/group/saveGroupByCustomerAndDepartment";
import updateGroupStatusById from "../../../resources/Api/group/updateGroupStatusById";
import findAllGroupsExportResponse from "../../../resources/Api/group/findAllGroupsExportResponse";
import { userAtom } from "../../../atoms/atoms";

import { departmentDefault, secretaryDefault } from "../../../consts/consts";
import userFindAllDepartmentsListResponse from "../../../resources/Api/user/userFindAllDepartmentsListResponse";
import userFindAllSecretariesListResponse from "../../../resources/Api/user/userFindAllSecretariesListResponse";
import { isImpersonationLocked } from "../../../utils/impersonation";

const columns: GridColDef[] = [
    {
        field: "activated",
        type: "boolean",
        width: 150,
        editable: false,
        sortable: false,
        renderHeader: () => (
            <Chip
                sx={{
                    color: "#ffff",
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={"Ativo"}
                icon={
                    <KeyOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />
                }
            />
        ),
        renderCell: (item) => (
            <Chip
                sx={{ background: "transparent" }}
                icon={
                    item.row.activated ? (
                        <CheckCircleRounded
                            className="activated"
                            style={{ height: 28, width: 28 }}
                        />
                    ) : (
                        <DoNotDisturbOnRounded
                            className="deactivated"
                            style={{ height: 28, width: 28 }}
                        />
                    )
                }
            />
        ),
    },
    {
        field: "name",
        align: "left",
        width: 200,
        editable: false,
        sortable: true,
        renderHeader: () => (
            <Chip
                sx={{
                    color: "#fff",
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={"Nome"}
                icon={
                    <GroupOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />
                }
            />
        ),
        renderCell: (item) => (
            <Chip
                sx={{
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={item.row.name}
            />
        ),
    },
    {
        field: "color",
        headerName: "cor",
        align: "left",
        headerClassName: "last-column",
        editable: false,
        sortable: true,
        renderHeader: () => (
            <Chip
                sx={{
                    color: "#fff",
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={"Cor"}
                icon={
                    <ColorLensOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />
                }
            />
        ),
        renderCell: (item) => (
            <Chip
                sx={{
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={
                    <Box
                        sx={{
                            background: item.row?.color,
                            height: 20,
                            width: 30,
                            borderRadius: 2,
                        }}
                    ></Box>
                }
            />
        ),
    },
];

const rowsOptions = (
    list: Array<VehicleGroupType>,
    openDetails?: SetterOrUpdater<boolean>
): Array<VehicleGroupType> => {
    const result = [...list];
    return result.map((item) => ({ ...item, openDetails }));
};

/**
 * GroupsPage - Screen that renders a page to create and manage vehicles groups
 * @component-screen
 */
const GroupsPage = ({ isHiddenMenu }: { isHiddenMenu: boolean }) => {
    // atoms
    const getCurrentUser = useRecoilValue<UserType>(userAtom);

    // functionalities states
    const [isOnline, setIsOnline] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);
    const [validationText, setTextValidation] = useState<string>("");
    const [isFieldsValid, setFieldsIsValid] = useState<boolean>(true);
    const [statusMessage, setStatusMessage] = useState<"warn" | "success" | "info" | "error" | undefined>("warn");
    const [isToastVisible, setIsToastVisible] = useState(false);
    const [inEdit, setInEdit] = useState<boolean>(false);
    const [inInsert, setInInsert] = useState<boolean>(false);
    const [isOpenDetails, setIsOpenDetails] = useState<boolean>(false);
    const [query, setQuery] = useState<string>("");
    const [filteredList, setFilteredList] = useState<Array<VehicleGroupType>>([]);
    const [isColorPickerVisible, setColorPickerIsVisible] = useState<boolean>(false);

    const [isLastPage, setIsLastPage] = useState<boolean>(true);
    const [numberOfElementsPerPage, setNumberOfElementsPerPage] = useState<number>(0);
    const [numberOfElements, setNumberOfElements] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(0);

    // states
    const [selectedGroup, setSelectedGroup] = useState<VehicleGroupType>({
        name: "",
        color: "",
        id: -1,
        departmentName: "",
        departmentSecretaryId: -1,
        departmentId: -1,
        vehicles: [],
    });

    const [groupsList, setGroupsList] = useState<Array<VehicleGroupType>>([]);
    const [vehicleList, setVehicleList] = useState<Array<VehicleListType>>([]);
    const [groupName, setGroupName] = useState<string>("");
    const [groupColor, setGroupColor] = useState<string>("");
    const [groupVehicles, setGroupVehicles] = useState<Array<string>>([]);
    const [report, setReport] = useState<ArrayBuffer>(new ArrayBuffer(0));
    const [exportType, setExportType] = useState<string>("");

    const [secretariesList, setSecretariesList] = useState<Array<SecretarySimpleType>>([]);
    const [departmentsList, setDepartmentsList] = useState<Array<DepartmentSimpleType>>([]);

    const [secretary, setSecretary] = useState<SecretarySimpleType>(secretaryDefault);
    const [department, setDepartment] = useState<DepartmentSimpleType>(departmentDefault);

    // refs
    const toastModalRef = useRef<Toast>(null);
    const toastRef = useRef<Toast>(null);

    // cookies
    const [cookies, _] = useCookies(["selectedParameters", "token"]);

    // hooks and consts
    const location = useLocation();

    const currentParameters: SelectedParametersType = useMemo(() => cookies.selectedParameters, [cookies.selectedParameters]);

    const handleClose = useCallback(() => {
        setColorPickerIsVisible(false);
        setLoading(false);
        setGroupColor("");
        setStatusMessage("warn");
        setIsToastVisible(false);
        setTextValidation("");
        setSecretary(secretaryDefault);
        setDepartment(departmentDefault);
        setGroupName("");
        setGroupVehicles([]);
        setIsOpenDetails(false);
        setSelectedGroup({
            name: "",
            color: "",
            id: -1,
            departmentSecretaryId: -1,
            departmentName: "",
            departmentId: -1,
            activated: true,
            vehicles: [],
        });
        setInEdit(false);
        setInInsert(false);
    }, []);

    useEffect(() => {
        const ref = isOpenDetails ? toastModalRef : toastRef;
        if (validationText && !isFieldsValid && !isToastVisible) {
            setToastRefToOpen(
                ref,
                validationText,
                "Atenção!",
                statusMessage,
                undefined,
                3000,
                false
            );
        }
    }, [
        isOpenDetails,
        validationText,
        isFieldsValid,
        statusMessage,
        isToastVisible
    ]);

    const getSecretariesList = useCallback(async () => {
        const token = cookies.token || sessionStorage.getItem("token");
        const userId = getCurrentUser.id;
        try {
            const response = await userFindAllSecretariesListResponse(
                token,
                userId
            );
            setSecretariesList(response.data);
        } catch (error) {
            console.log(error);
            setSecretariesList([]);
        }
    }, [cookies.token, getCurrentUser.id]);

    useEffect(() => {
        if (currentParameters?.isViewingAll) {
            getSecretariesList();
        }
    }, [getSecretariesList, currentParameters?.isViewingAll]);

    const getDepartmentsList = useCallback(async () => {
        const token = cookies.token || sessionStorage.getItem("token");
        const userId = getCurrentUser.id;

        try {
            const response =
                await userFindAllDepartmentsListResponse(
                    token,
                    userId,
                    secretary?.id
                );

            if (response.data.length > 0) {
                const selectedDepartment: DepartmentSimpleType = response.data.find(
                    (it: any) => it?.id === selectedGroup?.departmentId
                )!!;

                setDepartment(selectedDepartment);
                setDepartmentsList(response.data);
            }
        } catch (error) {
            console.log(error);
            setDepartmentsList([]);
        }
    }, [cookies.token, getCurrentUser.id, secretary.id, selectedGroup?.departmentId]);

    useEffect(() => {
        if (secretary?.name) {
            getDepartmentsList();
        }
    }, [getDepartmentsList, secretary?.name]);

    const getAllGroups = useCallback(
        async (search: string = "", status: string = "activated", page: number = 0, size: number = 100) => {
            const token: string = cookies.token || sessionStorage.getItem("token");
            setLoading(true);
            try {
                const { data } =
                    await findAllGroupsByCustomerIdAndSecretaryIdAndDepartmentIdCrudResponse(
                        token,
                        currentParameters.customer.id,
                        currentParameters.userId,
                        currentParameters.department.id,
                        status,
                        page,
                        size,
                        currentParameters?.isViewingAll
                    );
                setGroupsList(data.content);
                setIsLastPage(data.last);
                setNumberOfElementsPerPage(data.numberOfElements);
                setPageNumber(data.pageable.pageNumber);

                if (data.first) {
                    setNumberOfElements(data.totalElements);
                }
            } catch (error: any) {
                console.log(error);
                setGroupsList([]);
            } finally {
                setTimeout(() => {
                    setLoading(false);
                }, 2000);
            }
        },
        [currentParameters?.customer.id, currentParameters?.userId, currentParameters?.department.id, currentParameters?.isViewingAll, cookies.token]
    );

    useEffect(() => {
        getAllGroups();
    }, [getAllGroups]);

    const getAllVehicles = useCallback(
        async () => {
            const token: string = cookies.token || sessionStorage.getItem("token");
            const departmentId = (currentParameters?.department?.id == -1) ? department?.id : currentParameters?.department?.id;
            const isViewAll = inInsert && currentParameters.isViewingAll ? false : currentParameters?.isViewingAll;
            try {
                const { data } =
                    await findAllVehiclesByCustomerIdAndSecretaryIdAndDepartmentIdListResponse(
                        token,
                        currentParameters.customer.id,
                        departmentId,
                        isViewAll
                    );
                setVehicleList(data);
            } catch (error: any) {
                console.log(error);
                setVehicleList([]);
            }
        },
        [currentParameters.isViewingAll, currentParameters?.customer.id, currentParameters?.department.id, department?.id, cookies.token, inInsert]
    );

    useEffect(() => {
        if ((currentParameters.isViewingAll && secretary?.name && department?.name && inInsert) || (!inInsert && !inInsert && groupsList.length === 0)) {
            getAllVehicles();
        }
    }, [getAllVehicles, groupsList.length, inInsert, inEdit, secretary?.name, department?.name, currentParameters.isViewingAll]);

    const onSave = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        // const currentParameters: SelectedParametersType = cookies.selectedParameters;
        const departmentId = currentParameters?.isViewingAll ? department.id : currentParameters.department.id;

        setLoading(true);
        const data: GroupRequestType = {
            id: -1,
            name: groupName,
            color: groupColor,
            customerId: currentParameters.customer.id,
            departmentId: departmentId,
            vehicles: groupVehicles.map((car) => Number(car)),
        };

        if (
            !data.name ||
            !data.color ||
            data.vehicles.length === 0
        ) {
            setTextValidation(
                "Todos os campos marcados com * são obrigatórios, preencha e tente novamente."
            );
            setStatusMessage("warn");
            setFieldsIsValid(false);
            setLoading(false);
            return;
        }

        try {
            await saveGroupByCustomerAndDepartment(token, data);
            setTextValidation("Registro salvo com sucesso.");
            setStatusMessage("success");
            setFieldsIsValid(false);

            setTimeout(() => {
                handleClose();
                getAllGroups();
            }, 3000);
        } catch (error: any) {
            setLoading(false);
            const { data } = error.response;

            const errors = data.reason.split(".");
            errors.forEach((message: any, index: number) => {
                if (index === 0) {
                    setFieldsIsValid(false);
                    setStatusMessage("warn");
                    setTextValidation(message);
                }
            });
        }
    }, [
        currentParameters.customer.id,
        currentParameters?.department.id,
        currentParameters?.isViewingAll,
        department?.id,
        cookies?.token,
        groupName,
        groupColor,
        groupVehicles,
        handleClose,
        getAllGroups,
    ]);

    const onUpdate = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        // const currentParameters: SelectedParametersType = cookies.selectedParameters;

        setLoading(true);
        const data: GroupRequestType = {
            id: selectedGroup.id,
            name: groupName,
            color: groupColor,
            customerId: currentParameters.customer.id,
            departmentId: selectedGroup.departmentId,
            vehicles: groupVehicles.map((car) => Number(car)),
        };

        if (
            !data.name ||
            !data.color ||
            data.vehicles.length === 0
        ) {
            setTextValidation(
                "Todos os campos são obrigatórios, preencha e tente novamente."
            );
            setStatusMessage("warn");
            setFieldsIsValid(false);
            setLoading(false);
            return;
        }

        const vehicles = data.vehicles;
        const hasEqualLength = vehicles.length === selectedGroup.vehicles.length;
        const haveSameElements = vehicles.every((id) => selectedGroup.vehicles.includes(id));
        const hasChangesOnVehicles = !hasEqualLength || !haveSameElements;

        if (
            data.name === selectedGroup.name &&
            data.color === selectedGroup.color &&
            !hasChangesOnVehicles
        ) {
            setTextValidation("Nada foi alterado.");
            setStatusMessage("warn");
            setFieldsIsValid(false);
            setLoading(false);
            return;
        }

        try {
            await updateGroupByCustomerAndDepartment(token, data.id, data);
            setTextValidation("Registro atualizado com sucesso.");
            setStatusMessage("success");
            setFieldsIsValid(false);

            setTimeout(() => {
                handleClose();
                getAllGroups();
            }, 3000);
        } catch (error: any) {
            setLoading(false);
            const { data } = error.response;

            const errors = data.reason.split(".");
            errors.forEach((message: any, index: number) => {
                if (index === 0) {
                    setFieldsIsValid(false);
                    setStatusMessage("warn");
                    setTextValidation(message);
                }
            });
        }
    }, [
        currentParameters?.customer.id,
        getAllGroups,
        cookies.token,
        groupName,
        groupVehicles,
        groupColor,
        selectedGroup.name,
        selectedGroup.vehicles,
        selectedGroup.departmentId,
        selectedGroup.id,
        selectedGroup.color,
        handleClose
    ]);

    const onDelete = useCallback(
        async (id: number) => {
            const token: string = cookies.token || sessionStorage.getItem("token");
            const currentParameters: SelectedParametersType = cookies.selectedParameters;

            setLoading(true);
            try {
                await deleteGroupByIdAndCustomerAndDepartment(
                    token,
                    currentParameters.customer.id,
                    currentParameters.department.id,
                    id
                );
                setTextValidation("Registro excluído com sucesso.");
                setStatusMessage("success");
                setFieldsIsValid(false);
            } catch (error: any) {
                const { data } = error.response

                setLoading(false);
                setFieldsIsValid(false);
                setStatusMessage("warn");
                setTextValidation(data.reason);
            } finally {
                setTimeout(() => {
                    handleClose();
                    getAllGroups();
                }, 3000);
            }
        },
        [cookies.token, cookies.selectedParameters, getAllGroups, handleClose]
    );

    const onUpdateStatus = useCallback(
        async (id: number, status: string) => {
            const token: string = cookies.token || sessionStorage.getItem("token");
            // const currentParameters: SelectedParametersType = cookies.selectedParameters;
            setLoading(true);
            try {
                await updateGroupStatusById(
                    token,
                    id,
                    currentParameters.customer.id,
                    currentParameters.department.id,
                    status
                );
                setTextValidation("Registro atualizado com sucesso.");
                setStatusMessage("success");
                setFieldsIsValid(false);
            } catch (error: any) {
                const { data } = error.response

                setLoading(false);
                setFieldsIsValid(false);
                setStatusMessage("warn");
                setTextValidation(data.reason);
            } finally {
                setTimeout(() => {
                    handleClose();
                    getAllGroups();
                }, 8000);
            }
        },
        [cookies.token, currentParameters?.customer.id, currentParameters?.department.id, getAllGroups, handleClose]
    );

    const getReport = useCallback(async (type: string) => {
        setLoading(true);

        let reportType = type;
        let isSimpleType = false;
        if (type.includes("simple")) {
            reportType = reportType.split(" ")[0];
            isSimpleType = true;
        };
        // simpleXlsxExport = false

        let dataBuffer = new ArrayBuffer(0);
        try {
            const { data } = await findAllGroupsExportResponse(cookies.token,
                cookies.selectedParameters?.customer.id,
                cookies.selectedParameters?.department.id,
                groupsList.length > 0 && groupsList[0].activated
                    ? "activated"
                    : "deactivated", reportType, cookies.selectedParameters?.isViewingAll, isSimpleType);

            if ((data as ArrayBuffer).byteLength === 0) {
                setTextValidation("Nenhum dado para mostrar no relatório.");
                setStatusMessage("warn");
                setFieldsIsValid(false);
            } else {
                setTextValidation("Relatório gerado com sucesso.");
                setStatusMessage("success");
                setFieldsIsValid(false);
                dataBuffer = data;
            }
        } catch (error: any) {
            const { data } = error.response

            setLoading(false);
            setFieldsIsValid(false);
            setStatusMessage("warn");
            setTextValidation(data.reason);
        } finally {
            setTimeout(() => {
                handleClose();
                setReport(dataBuffer);
                setExportType(type);
            }, 3000);
        }
    }, [cookies.token, cookies.selectedParameters?.customer.id, cookies.selectedParameters?.department.id, cookies.selectedParameters?.isViewingAll, groupsList, handleClose]);

    useEffect(() => {
        if (report.byteLength > 1) {
            const typeExport = (exportType === "pdf" ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
            const file = new Blob([report], { type: typeExport });
            const fileURL = URL.createObjectURL(file);
            window.open(fileURL);
            setReport(new ArrayBuffer(0));
        }
    }, [report, exportType]);

    const verifyBeforeUpdateStatus = useCallback(
        (id: number, status: string) => {
            const ref = toastModalRef;
            if (!isToastVisible) {
                setToastRefToOpen(
                    ref,
                    "Deseja realmente atualizar o status desse registro?",
                    "Atenção!",
                    statusMessage,
                    {
                        content: (props: ContentProps) => (
                            <Box
                                className="flex flex-column align-items-left"
                                style={{ flex: "1" }}
                            >
                                <Typography>{props?.message.summary}</Typography>
                                <Box className="font-medium text-lg my-3 text-900">
                                    {props?.message.detail}
                                </Box>
                                <Button
                                    className="p-button-sm flex"
                                    style={{ cursor: "pointer", color: "inherit" }}
                                    onClick={() => {
                                        ref.current?.clear();
                                        setIsToastVisible(false);
                                        onUpdateStatus(id, status);
                                    }}
                                >
                                    Sim
                                </Button>
                                <Button
                                    className="p-button-sm flex"
                                    style={{ cursor: "pointer", color: "inherit" }}
                                    onClick={() => {
                                        ref.current?.clear();
                                        setIsToastVisible(false);
                                    }}
                                >
                                    Não
                                </Button>
                            </Box>
                        ),
                    },
                    undefined,
                    true
                );
                setIsToastVisible(true);
            }
        },
        [onUpdateStatus, isToastVisible, statusMessage]
    );

    const verifyBeforeDelete = useCallback(
        (id: number) => {
            const ref = toastModalRef;
            if (!isToastVisible) {
                setToastRefToOpen(
                    ref,
                    "O registro será excluído permanentemente. Deseja realmente excluir?",
                    "Atenção!",
                    statusMessage,
                    {
                        content: (props: ContentProps) => (
                            <Box
                                className="flex flex-column align-items-left"
                                style={{ flex: "1" }}
                            >
                                <Typography>{props?.message.summary}</Typography>
                                <Box className="font-medium text-lg my-3 text-900">
                                    {props?.message.detail}
                                </Box>
                                <Button
                                    className="p-button-sm flex"
                                    style={{ cursor: "pointer", color: "inherit" }}
                                    onClick={() => {
                                        ref.current?.clear();
                                        setIsToastVisible(false);
                                        onDelete(id);
                                    }}
                                >
                                    Sim
                                </Button>
                                <Button
                                    className="p-button-sm flex"
                                    style={{ cursor: "pointer", color: "inherit" }}
                                    onClick={() => {
                                        ref.current?.clear();
                                        setIsToastVisible(false);
                                    }}
                                >
                                    Não
                                </Button>
                            </Box>
                        ),
                    },
                    undefined,
                    true
                );
                setIsToastVisible(true);
            }
        },
        [onDelete, isToastVisible, statusMessage]
    );

    const verifyBeforeCancel = useCallback(
        (selected: VehicleGroupType) => {
            const ref = toastModalRef;
            let canCancel = true;

            if (inEdit && !inInsert) {
                const vehicles = groupVehicles.map((car) => Number(car));
                const hasEqualLength = vehicles.length === selected.vehicles.length;
                const haveSameElements = vehicles.every((id) => selected.vehicles.includes(id));
                const hasChangesOnVehicles = !hasEqualLength || !haveSameElements;

                if (
                    groupName !== selected.name ||
                    groupColor !== selected.color ||
                    hasChangesOnVehicles
                ) {
                    canCancel = false;
                }
            } else if (
                !inEdit &&
                inInsert &&
                (groupName || groupVehicles.length > 0)
            ) {
                canCancel = false;
            }

            if (!canCancel) {
                if (!isToastVisible) {
                    setToastRefToOpen(
                        ref,
                        "Existem dados não salvos, deseja realmente cancelar?",
                        "Atenção!",
                        statusMessage,
                        {
                            content: (props: ContentProps) => (
                                <Box
                                    className="flex flex-column align-items-left"
                                    style={{ flex: "1" }}
                                >
                                    <Typography>{props?.message.summary}</Typography>
                                    <Box className="font-medium text-lg my-3 text-900">
                                        {props?.message.detail}
                                    </Box>
                                    <Button
                                        className="p-button-sm flex"
                                        style={{ cursor: "pointer", color: "inherit" }}
                                        onClick={() => {
                                            ref.current?.clear();
                                            handleClose();
                                        }}
                                    >
                                        Sim
                                    </Button>
                                    <Button
                                        className="p-button-sm flex"
                                        style={{ cursor: "pointer", color: "inherit" }}
                                        onClick={() => {
                                            ref.current?.clear();
                                            setIsToastVisible(false);
                                        }}
                                    >
                                        Não
                                    </Button>
                                </Box>
                            ),
                        },
                        undefined,
                        true
                    );
                    setIsToastVisible(true);
                }
            } else {
                handleClose();
            }
        },
        [
            inEdit,
            inInsert,
            handleClose,
            statusMessage,
            isToastVisible,
            groupName,
            groupColor,
            groupVehicles,
        ]
    );

    const handleOnEditValues = useCallback((selectedGroup: VehicleGroupType) => {
        if (selectedGroup.name && !inInsert) {
            setGroupName(selectedGroup.name);
            setGroupColor(selectedGroup.color);
            setGroupVehicles(selectedGroup.vehicles.map((car) => String(car)));

            if (currentParameters?.isViewingAll) {
                if (secretariesList.length > 0) {
                    const selectedSecretary: SecretarySimpleType = secretariesList.find((it) => it?.id === selectedGroup?.departmentSecretaryId)!!;
                    setSecretary(selectedSecretary);
                }
            }
        }
    }, [inInsert, secretariesList, currentParameters?.isViewingAll]);

    useEffect(() => {
        setFilteredList(getFilteredList(["name"], groupsList, query));
    }, [groupsList, query]);

    const handleChange = useCallback((name: string, value: string) => {
        if (name === "name") {
            setGroupName(value);
        }
        if (name === "color") {
            setGroupColor(value);
        }
    }, []);

    const loadingEsqueleton = useMemo(() => {
        return (
            <Grid
                className="container "
                sx={{
                    height: 650,
                    width: 950,
                }}
            >
                {Array.from(new Array(1)).map((_, i) => (
                    <Box key={i} sx={{ width: "100%", height: 650, padding: 5 }}>
                        <Skeleton height={56} />
                        <Skeleton height={56} />
                        <Skeleton height={56} />
                        <Skeleton variant="rectangular" width={"100%"} height={350} />
                    </Box>
                ))}
            </Grid>
        );
    }, []);

    const DetailsModal = useMemo(() => {
        return (
            <Rmodal
                open={isOpenDetails}
                hiddenBackdrop={false}
                toast={
                    <Toast
                        ref={toastModalRef}
                        position="center"
                        onShow={() => {
                            setIsToastVisible(true);

                            if (validationText) {
                                setTimeout(() => {
                                    setFieldsIsValid(true);
                                    setIsToastVisible(false);
                                }, 3000);
                            }
                        }}
                    />
                }
            >
                <Paper
                    className="tw-flex tw-flex-col tw-overflow-hidden tw-rounded-2xl tw-bg-white tw-shadow-2xl tw-transition-all tw-duration-300 tw-ease-gtr-smooth"
                    sx={style}
                >
                    <Box
                        className="row tw-m-0 tw-flex-1 tw-overflow-y-auto tw-bg-white tw-p-4 sm:tw-p-6"
                        sx={{
                            margin: 0,
                            width: "100%",
                            flex: 1,
                            overflowY: "auto",
                            backgroundColor: "#ffff",
                            boxShadow: "none",
                            padding: { xs: 2, sm: 3 },
                            scrollbarWidth: "thin",
                            scrollbarColor: "#006C77 transparent",
                            "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "#ffff", transition: "box-shadow 200ms ease, background-color 200ms ease" },
                            "& .MuiOutlinedInput-root:hover": { backgroundColor: "rgba(0, 108, 119, 0.02)" },
                            "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: "0 0 0 3px rgba(0, 108, 119, 0.10)" },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#006C77" },
                        }}
                    >
                        {!loading ? (
                            <>
                                <Box className="col-sm-12 col-md-7 d-none d-md-flex">
                                    <Typography variant="body1" sx={{ fontWeight: "bolder", marginBottom: 5 }}>
                                        Identificação
                                    </Typography>
                                </Box>
                                <Box
                                    className="col-sm-12 col-md-5"
                                    style={{ display: "flex", justifyContent: "end" }}
                                >
                                    {selectedGroup?.name && (
                                        <>
                                            <Chip
                                                style={{
                                                    color: selectedGroup.activated
                                                        ? "#00B700"
                                                        : "#dc3545",
                                                    marginRight: 5,
                                                    width: "50%",
                                                    fontWeight: "bolder",
                                                }}
                                                clickable={false}
                                                onClick={() => { }}
                                                label={selectedGroup.activated ? "Ativo" : "Inativo"}
                                            />
                                            {selectedGroup.activated && (
                                                <Chip
                                                    style={{
                                                        color: "black",
                                                        marginRight: 5,
                                                        width: "50%",
                                                    }}
                                                    disabled={!location?.state?.canEdit || isImpersonationLocked()}
                                                    onClick={() => {
                                                        setInEdit(true);
                                                    }}
                                                    label="Editar"
                                                    icon={
                                                        <DriveFileRenameOutlineOutlined
                                                            style={{ height: 24, width: 24, fill: "black" }}
                                                        />
                                                    }
                                                />
                                            )}
                                            {false && (
                                                <Chip
                                                    style={{
                                                        color: "black",
                                                        marginRight: 5,
                                                        width: "50%",
                                                    }}
                                                    disabled={!location?.state?.canDelete}
                                                    onClick={() => {
                                                        if (!isToastVisible) {
                                                            verifyBeforeDelete(selectedGroup.id);
                                                        }
                                                    }}
                                                    label="Excluir"
                                                    icon={
                                                        <DeleteForeverOutlined
                                                            style={{ height: 24, width: 24, fill: "black" }}
                                                        />
                                                    }
                                                />
                                            )}

                                            <Chip
                                                style={{
                                                    color: "black",
                                                    marginRight: 5,
                                                    width: "50%",
                                                }}
                                                disabled={!location?.state?.canEdit || isImpersonationLocked()}
                                                onClick={() => {
                                                    if (!isToastVisible) {
                                                        verifyBeforeUpdateStatus(
                                                            selectedGroup.id,
                                                            selectedGroup.activated
                                                                ? "deactivate"
                                                                : "activate"
                                                        );
                                                    }
                                                }}
                                                label={selectedGroup.activated ? "Desativar" : "Ativar"}
                                                icon={
                                                    <RemoveCircleOutlineOutlined
                                                        style={{ height: 24, width: 24, fill: "black" }}
                                                    />
                                                }
                                            />
                                        </>
                                    )}
                                </Box>

                                {currentParameters?.isViewingAll && inInsert && <>
                                    <Box className="col-md-6" style={{ marginBottom: 10 }}>
                                        <Autocomplete
                                            id="chip-label-select-secretary"
                                            className="input-select-secretary"
                                            style={{
                                                width: "100%",
                                                minHeight: "56px",
                                                height: "56px",
                                                backgroundColor: "#ffffff29",
                                                borderColor: "lightgray",
                                            }}
                                            options={secretariesList}
                                            value={secretary}
                                            onChange={(_, option) => {
                                                setSecretary(option ?? secretaryDefault);
                                                setDepartment(departmentDefault);
                                            }}
                                            disabled={(inEdit || (!inEdit && !inInsert)) as boolean}
                                            getOptionLabel={(option) => `${option.reducedName}`}
                                            noOptionsText={"Nada encontrado"}
                                            renderInput={(params) => (
                                                <>
                                                    <FormControl
                                                        className="form-group"
                                                        style={{ display: "flex" }}
                                                    >
                                                        <InputLabel
                                                            id="country-label"
                                                            required
                                                            style={{ color: "rgba(0, 0, 0, 0.6)", fontFamily: "Prometo" }}
                                                            shrink
                                                        >
                                                            Secretaria
                                                        </InputLabel>
                                                        <TextField
                                                            {...params}
                                                            variant="outlined"
                                                            placeholder="Selecione uma secretaria"
                                                            autoComplete="off"
                                                            required
                                                            InputLabelProps={{ shrink: true }}
                                                            InputProps={{
                                                                ...params.InputProps,
                                                                notched: true,
                                                                label: "Secretaria",
                                                                classes: {
                                                                    notchedOutline: "custom-notch",
                                                                },
                                                            }}
                                                        />
                                                    </FormControl>
                                                </>
                                            )}
                                        />
                                    </Box>

                                    <Box className="col-md-6" style={{ marginBottom: 10 }}>
                                        <Autocomplete
                                            id="chip-label-select-department"
                                            className="input-select-department"
                                            style={{
                                                width: "100%",
                                                minHeight: "56px",
                                                height: "56px",
                                                backgroundColor: "#ffffff29",
                                                borderColor: "lightgray",
                                            }}
                                            options={departmentsList}
                                            value={department}
                                            onChange={(_, option) => {
                                                setDepartment(option ?? departmentDefault);
                                            }}
                                            disabled={(inEdit || (!inEdit && !inInsert)) as boolean}
                                            getOptionLabel={(option) => `${option.reducedName}`}
                                            noOptionsText={"Nada encontrado"}
                                            renderInput={(params) => (
                                                <>
                                                    <FormControl
                                                        className="form-group"
                                                        style={{ display: "flex" }}
                                                    >
                                                        <InputLabel
                                                            id="country-label"
                                                            required
                                                            style={{ color: "rgba(0, 0, 0, 0.6)", fontFamily: "Prometo" }}
                                                            shrink
                                                        >
                                                            Departamento
                                                        </InputLabel>
                                                        <TextField
                                                            {...params}
                                                            variant="outlined"
                                                            placeholder="Selecione um departamento"
                                                            autoComplete="off"
                                                            required
                                                            InputLabelProps={{ shrink: true }}
                                                            InputProps={{
                                                                ...params.InputProps,
                                                                notched: true,
                                                                label: "Departamento",
                                                                classes: {
                                                                    notchedOutline: "custom-notch",
                                                                },
                                                            }}
                                                        />
                                                    </FormControl>
                                                </>
                                            )}
                                        />
                                    </Box></>}

                                <Box
                                    className="col-md-12"
                                    style={{ margin: "15px 0px 15px 0px" }}
                                >
                                    <FormControl
                                        className="form-group"
                                        style={{ display: "flex", width: "100%" }}
                                    >
                                        <InputLabel
                                            id="demo-multiple-password-label"
                                            required
                                            shrink
                                            disabled={
                                                (selectedGroup?.name && !inEdit && !inInsert) as boolean
                                            }
                                            style={{ fontFamily: "Prometo" }}
                                        >
                                            Nome
                                        </InputLabel>
                                        <TextField
                                            name="name"
                                            autoComplete="off"
                                            required
                                            InputProps={{
                                                label: "Nome",
                                                notched: true,
                                                style: { fontFamily: "Prometo" },
                                            }}
                                            disabled={
                                                (selectedGroup?.name && !inEdit && !inInsert) as boolean
                                            }
                                            value={groupName}
                                            onChange={(e) => {
                                                handleChange(e.target.name, e.target.value);
                                            }}
                                            type="text"
                                            className="form-control form-control-login"
                                            placeholder="Digite o nome"
                                        />
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            className="visibility-password"
                                            disabled
                                            onMouseDown={(e: SyntheticEvent) => { }}
                                            edge="end"
                                            size="large"
                                        >
                                            <GroupOutlined />
                                        </IconButton>
                                    </FormControl>
                                </Box>
                                <Box
                                    className="col-md-12"
                                    style={{ margin: "15px 0px 20px 0px" }}
                                >
                                    <Autocomplete
                                        id="chip-label-select-vehicle"
                                        className="input-select-vehicle"
                                        disabled={
                                            (selectedGroup?.name && !inEdit && !inInsert) as boolean
                                        }
                                        multiple
                                        limitTags={2}
                                        onChange={(_, value) => {
                                            setGroupVehicles(value.map((a) => String(a.id)));
                                        }}
                                        groupBy={(option) => option.vehicleTypeName}
                                        value={vehicleList.filter((a) =>
                                            groupVehicles.includes(String(a.id))
                                        )}
                                        options={vehicleList.sort((a, b) =>
                                            -b.vehicleTypeName.localeCompare(a.vehicleTypeName)
                                        )}
                                        getOptionLabel={(option) =>
                                            `${option?.name} - ${option?.vehiclePlate}(${option?.deviceImei ?? option?.satelliteImei ?? "Sem dispositivo"})`
                                        }
                                        noOptionsText={"Nada encontrado"}
                                        sx={{
                                            width: "100%",
                                            // área onde ficam os chips
                                            "& .MuiAutocomplete-inputRoot": {
                                                display: "flex",
                                                border: "none",
                                                borderColor: "transparent",
                                                flexWrap: "wrap",
                                                alignItems: "flex-start",
                                                gap: "4px",
                                                maxHeight: "100px",
                                                overflowY: "auto",
                                                overflowX: "hidden",
                                                scrollbarWidth: "thin",
                                                scrollBehavior: "smooth",
                                                paddingRight: "6px",
                                            },

                                            "& .MuiAutocomplete-tag": {
                                                maxWidth: "calc(50% - 8px)",
                                            },

                                            "& .MuiAutocomplete-inputRoot::-webkit-scrollbar": {
                                                width: "6px",
                                            },
                                            "& .MuiAutocomplete-inputRoot::-webkit-scrollbar-track": {
                                                background: "transparent",
                                            },
                                            "& .MuiAutocomplete-inputRoot::-webkit-scrollbar-thumb": {
                                                backgroundColor: "rgba(0,0,0,0.25)",
                                                borderRadius: "8px",
                                            },
                                            "& .MuiAutocomplete-inputRoot::-webkit-scrollbar-thumb:hover": {
                                                backgroundColor: "rgba(0,0,0,0.4)",
                                            },
                                        }}
                                        ListboxProps={{
                                            style: {
                                                maxHeight: 250,
                                                overflowY: "auto",
                                                border: "none"
                                            },
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        "& .MuiOutlinedInput-notchedOutline": {
                                                            borderColor: "lightgray",
                                                        },

                                                        "&:hover .MuiOutlinedInput-notchedOutline": {
                                                            borderColor: "#999",
                                                        },

                                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                            borderColor: "#ffffff00",
                                                        },

                                                        "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                                                            borderColor: "#d32f2f",
                                                        },
                                                    },
                                                }}
                                                variant="outlined"
                                                placeholder="Selecione um veículo"
                                                autoComplete="off"
                                                required
                                                InputLabelProps={{ shrink: true }}
                                                label="Veículos"
                                            />
                                        )}
                                    />
                                </Box>
                                <Box className="col-sm-12 col-md-7 d-none d-md-flex">
                                    <Typography variant="body1" sx={{ fontWeight: "bolder" }}>
                                        Informações adicionais
                                    </Typography>
                                </Box>
                                <Box
                                    className="col-sm-12 col-md-5"
                                    style={{ display: "flex", justifyContent: "end" }}
                                ></Box>
                                <Box
                                    className="col-md-12"
                                    style={{
                                        margin: "15px 0px 5px 0px",
                                        display: "flex",
                                        height: 330,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            padding: 0,
                                            "& .leaflet-container": {
                                                position: "relative !important",
                                            },
                                        }}
                                    >
                                        <Box
                                            style={{
                                                height: 35,
                                                width: 220,
                                                borderRadius: 5,
                                                color: "#ffff",
                                                backgroundColor: groupColor || "rgb(0, 63, 74)",
                                                marginBottom: 5,
                                                display: "flex",
                                                justifyContent: "start",
                                                alignItems: "center",
                                                transition: "all 0.5s ease-out allow-discrete",
                                                cursor: "pointer",
                                                padding: 5,
                                            }}
                                            onClick={() =>
                                                setColorPickerIsVisible(!isColorPickerVisible)
                                            }
                                            className="visibility-color"
                                        >
                                            <ColorLensOutlined />{" "}
                                        </Box>
                                        <Box
                                            style={{
                                                transition: "all 0.5s ease-out allow-discrete",
                                                opacity:
                                                    (inEdit && !isColorPickerVisible) ||
                                                        (inInsert && !isColorPickerVisible)
                                                        ? 1
                                                        : 0,
                                            }}
                                        >
                                            {" "}
                                            <SketchPicker
                                                color={groupColor || "rgb(0, 63, 74)"}
                                                disableAlpha
                                                onChange={(color) => {
                                                    ((inEdit && !inInsert) || inInsert) &&
                                                        handleChange("color", color.hex);
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                    <Tooltip
                                        title={
                                            "Cor usada par customização do grupo, exibição das cercas virtuais e etc"
                                        }
                                        style={{ color: "black", margin: 5, cursor: "pointer" }}
                                    >
                                        <HelpOutlineOutlined />
                                    </Tooltip>
                                </Box>
                            </>
                        ) : (
                            loadingEsqueleton
                        )}
                    </Box>
                    <Box
                        className="tw-flex tw-w-full tw-flex-none tw-justify-end tw-border-t tw-border-black/10 tw-bg-white tw-p-4"
                        sx={{ display: "flex", width: "100%", alignSelf: "stretch", justifyContent: "flex-end", padding: 2, borderTop: "1px solid rgba(0, 0, 0, 0.10)", backgroundColor: "#ffff" }}
                    >
                        <Paper
                            className="tw-flex tw-w-full tw-flex-col-reverse tw-gap-2 sm:tw-w-auto sm:tw-flex-row"
                            elevation={0}
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column-reverse", sm: "row" },
                                justifyContent: "flex-end",
                                width: { xs: "100%", sm: "auto" },
                                marginLeft: "auto",
                                alignSelf: "flex-end",
                                gap: 1,
                                background: "transparent",
                            }}
                        >
                            <Button
                                type="button"
                                style={{
                                    fontFamily: "Prometo",
                                    color: "#ffff",
                                    marginRight: 5,
                                }}
                                onClick={() => {
                                    if (!isToastVisible) {
                                        verifyBeforeCancel(selectedGroup);
                                    }
                                }}
                                className="btn btn-primary btn-block login-button-close tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"
                            >
                                <CloseOutlined style={{ marginRight: 5 }} />
                                <Typography
                                    className="forgot-password"
                                    style={{
                                        fontFamily: "Prometo",
                                        fontSize: "16px",
                                        color: "#ffff",
                                    }}
                                >
                                    Fechar
                                </Typography>
                            </Button>
                            <Button
                                type="button"
                                style={{ fontFamily: "Prometo", color: "#ffff" }}
                                disabled={!inEdit && !inInsert}
                                onClick={() => {
                                    inInsert && !inEdit ? onSave() : onUpdate();
                                }}
                                className="btn btn-primary btn-block login-button tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"
                            >
                                <SaveOutlined style={{ marginRight: 5 }} />
                                <Typography
                                    className="forgot-password"
                                    style={{
                                        fontFamily: "Prometo",
                                        fontSize: "16px",
                                        color: "#ffff",
                                    }}
                                >
                                    Salvar
                                </Typography>
                            </Button>
                        </Paper>
                    </Box>
                </Paper>
            </Rmodal>
        );
    }, [
        department,
        departmentsList,
        secretariesList,
        secretary,
        currentParameters?.isViewingAll,
        verifyBeforeUpdateStatus,
        verifyBeforeDelete,
        isColorPickerVisible,
        onUpdate,
        verifyBeforeCancel,
        location?.state?.canDelete,
        location?.state?.canEdit,
        isToastVisible,
        validationText,
        selectedGroup,
        isOpenDetails,
        loading,
        vehicleList,
        groupName,
        groupColor,
        groupVehicles,
        handleChange,
        onSave,
        loadingEsqueleton,
        inInsert,
        inEdit,
    ]);

    return (
        <>
            {DetailsModal}
            <Toast
                ref={toastRef}
                position="center"
                onShow={() => {
                    setIsToastVisible(true);

                    if (validationText) {
                        setTimeout(() => {
                            setFieldsIsValid(true);
                            setIsToastVisible(false);
                        }, 3000);
                    }
                }}
            />
            <GridTemplate
                columns={columns}
                headerName="Grupos"
                isHiddenMenu={isHiddenMenu}
                isLoading={loading}
                order={"asc"}
                rows={rowsOptions(filteredList, (item: any) => {
                    setIsOpenDetails(true);
                    setSelectedGroup(item);
                })}
                sortField="name"
                withFilter={true}
                withOptions={true}
                query={query}
                setQuery={(value: string) => setQuery(value)}
                filterPlaceholder={"Nome"}
                onRefresh={getAllGroups}
                onExport={getReport}
                disableAddButton={!location?.state?.canCreate}
                handleNext={(activated: string, page: number, size: number) => getAllGroups("", activated, page, size)}
                handlePrevious={() => { }}
                isLastPage={isLastPage}
                totalPerPage={numberOfElementsPerPage}
                currentPage={pageNumber}
                totalElements={numberOfElements}
                onAdd={() => {
                    setIsOpenDetails(true);
                    setInInsert(true);
                    setLoading(true);
                    setTimeout(() => {
                        setLoading(false);
                    }, 2000);
                }}
                onClickRow={(item: any) => {
                    setIsOpenDetails(true);
                    setSelectedGroup(item);
                    handleOnEditValues(item);
                    setLoading(true);
                    setTimeout(() => {
                        setLoading(false);
                    }, 2000);
                }}
            />
        </>
    );
};

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    display: "flex",
    flexDirection: "column",
    width: { xs: "calc(100vw - 24px)", sm: "min(94vw, 960px)" },
    maxWidth: 960,
    maxHeight: "calc(100dvh - 24px)",
    overflow: "hidden",
    bgcolor: "#ffff",
    border: "none",
    boxShadow: "none",
    borderRadius: 4,
    color: "black",
    justifyContent: "center",
    alignItems: "center",
    lineBreak: "anywhere",
    padding: 0,
    fontFamily: "Prometo",
};

export default GroupsPage;
